import { Loader2 } from 'lucide-react'
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { config } from '@/config'
import { ApiService } from '@/service/api'
import { KgTraceApi } from './api'
import { NegativeEvidence } from './components/NegativeEvidence'
import { SourceViewer } from './components/SourceViewer'
import { StepInspector } from './components/StepInspector'
import { TraceTimeline } from './components/TraceTimeline'
import type { TraceFileResponse, TraceResponse, TraceStepFull } from './types'

/**
 * `/kg-trace/:correlation_id` — agent execution forensics page
 * (AGNT-685, parent AGNT-683).
 *
 * v1 (this PR): timeline + meta header. Step inspector + source
 * viewer land in PRs 2f / 2g; selecting a
 * step in the timeline is wired today but the right pane just
 * shows the selected step id until those PRs land.
 *
 * Standalone page, not embedded in any existing layout — no nav,
 * no sidebar. Designed for "open in a new tab from chat / eval
 * report" engineers debugging a specific failed query.
 */

// KG traces live only in Langfuse, whose SDK batches span writes — so there's
// a write→read lag of up to ~30s between the agent answering and the trace
// being queryable. Opening the viewer inside that window returns a 404 (which
// `KgTraceApi.getTrace` maps to `null`). Rather than treat that as terminal,
// we poll through the ingestion window: a 404 fired ~4s after the answer flips
// to a 200 by ~35s (observed in the logs, AGNT-685). 2s × 20 attempts ≈ 38s of
// coverage, comfortably past the observed ~30s window.
export const POLL_INTERVAL_MS = 2000
export const MAX_POLL_ATTEMPTS = 20

export default function KgTracePage(): ReactElement {
  const { correlationId } = useParams<{ correlationId: string }>()
  const [trace, setTrace] = useState<TraceResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null)
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null)
  // Bumped by the Retry button to restart the poll loop from attempt 0.
  const [reloadNonce, setReloadNonce] = useState<number>(0)

  const api = useMemo(() => new KgTraceApi(new ApiService(config.apiBaseUrl)), [])

  const fetchFile = useCallback(
    (path: string): Promise<TraceFileResponse | null> => {
      if (!correlationId) return Promise.resolve(null)
      return api.getFile(correlationId, path)
    },
    [api, correlationId],
  )

  const fetchStepFull = useCallback(
    (stepId: string): Promise<TraceStepFull | null> => {
      if (!correlationId) return Promise.resolve(null)
      return api.getStepFull(correlationId, stepId)
    },
    [api, correlationId],
  )

  useEffect(() => {
    if (!correlationId) {
      setError('Missing correlation_id in URL.')
      setLoading(false)
      return
    }

    // Restart in the loading state on every (re)run — a manual Retry bumps
    // `reloadNonce`, which re-runs this effect from attempt 0.
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0
    setLoading(true)
    setError(null)
    // Clear any previously-loaded trace so SPA navigation to a new
    // correlation id can't flash the stale trace in the gap between the
    // re-render (new id from useParams) and this effect firing.
    setTrace(null)

    // Self-scheduling poll: a `null` (404) means the trace hasn't been
    // ingested into Langfuse yet, so keep the loader up and retry on an
    // interval until a non-null trace arrives or the budget is exhausted.
    // A thrown error is a *real* failure (non-404) — surface it immediately
    // and stop; we never retry real failures.
    const poll = (): void => {
      api
        .getTrace(correlationId)
        .then((result) => {
          if (cancelled) return
          if (result) {
            setTrace(result)
            // Auto-select the first step so the inspector pane (PR 2f)
            // has something to show on initial render.
            setSelectedStepId(result.steps[0]?.step_id ?? null)
            setLoading(false)
            return
          }
          attempt += 1
          if (attempt >= MAX_POLL_ATTEMPTS) {
            setError(
              `Trace not found for correlation_id=${correlationId} (it may still be processing — try again).`,
            )
            setLoading(false)
            return
          }
          timer = setTimeout(poll, POLL_INTERVAL_MS)
        })
        .catch((err: unknown) => {
          if (cancelled) return
          setError(err instanceof Error ? err.message : 'Failed to load trace.')
          setLoading(false)
        })
    }

    poll()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
    // `api` is stable (useMemo with [] deps) but include it for
    // exhaustive-deps correctness — if a future edit changes the
    // memoization, the effect picks up the new instance. `reloadNonce`
    // re-runs the effect when the user clicks Retry.
  }, [api, correlationId, reloadNonce])

  if (loading) {
    return (
      <div
        className="p-8 flex items-center gap-3 text-muted-foreground"
        data-testid="kg-trace-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        <span>Loading trace… this can take a few seconds after the agent responds.</span>
      </div>
    )
  }

  if (error || !trace) {
    return (
      <div className="p-8" data-testid="kg-trace-error">
        <h1 className="text-xl font-semibold text-red-700 dark:text-red-400 mb-2">Trace unavailable</h1>
        <p className="text-sm text-muted-foreground">{error ?? 'Trace not found.'}</p>
        <p className="text-xs text-muted-foreground mt-4 font-mono">
          correlation_id: {correlationId ?? '(missing)'}
        </p>
        {correlationId && (
          <button
            type="button"
            onClick={() => setReloadNonce((n) => n + 1)}
            className="mt-4 inline-flex items-center rounded border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted"
            data-testid="kg-trace-retry"
          >
            Retry
          </button>
        )}
      </div>
    )
  }

  const selectedStep = trace.steps.find((s) => s.step_id === selectedStepId) ?? null

  return (
    <div className="h-screen flex flex-col bg-background text-foreground" data-testid="kg-trace-page">
      <TraceHeader trace={trace} />
      <NegativeEvidence
        steps={trace.steps}
        onSelectStep={(id) => {
          setSelectedStepId(id)
          setSelectedFilePath(null)
        }}
      />
      <div className="flex-1 flex min-h-0">
        <aside className="w-96 border-r overflow-y-auto" data-testid="kg-trace-timeline-pane">
          <TraceTimeline
            steps={trace.steps}
            selectedStepId={selectedStepId}
            onSelect={(id) => {
              setSelectedStepId(id)
              setSelectedFilePath(null)
            }}
          />
        </aside>
        <section
          className="flex-1 overflow-y-auto p-6 border-r"
          data-testid="kg-trace-inspector-pane"
        >
          {selectedStep ? (
            <StepInspector
              // Remount on step change so the loaded-full-payload state (and
              // collapsible open state) resets — otherwise step A's loaded body
              // would bleed into step B (greptile #18118).
              key={selectedStep.step_id}
              step={selectedStep}
              onFilePathClick={setSelectedFilePath}
              selectedFilePath={selectedFilePath}
              onLoadFullPayload={fetchStepFull}
            />
          ) : (
            <p className="text-sm text-muted-foreground italic">Select a step to inspect.</p>
          )}
        </section>
        <section
          className="flex-1 flex flex-col min-w-0"
          data-testid="kg-trace-source-pane"
        >
          <div className="flex-1 min-h-0">
            <SourceViewer fetchFile={fetchFile} path={selectedFilePath} />
          </div>
        </section>
      </div>
    </div>
  )
}

/**
 * Top strip — correlation id, git_sha, timing, steps + the query
 * the user asked and the final answer the agent produced (PR J).
 *
 * The Q+A row sits beneath the meta strip so the meta scalars stay
 * scannable. Both render in italic muted text when the join missed
 * (e.g., the conversation_id couldn't be resolved or the timestamp
 * lookup found nothing) — the rest of the trace still loads.
 */
/**
 * Tone per post-processor verdict kind. `grounded` reads as success (emerald),
 * the abstaining kinds as caution (amber). An unknown kind falls back to a
 * neutral tone rather than being mis-colored as amber — so a new backend
 * verdict shows up visibly un-styled instead of masquerading as a known one.
 */
const VERDICT_TONES: Record<string, string> = {
  grounded: 'bg-emerald-100 text-emerald-800',
  punt: 'bg-amber-100 text-amber-800',
  abstain: 'bg-amber-100 text-amber-800',
}
const VERDICT_TONE_DEFAULT = 'bg-gray-100 text-gray-700'

export function TraceHeader({ trace }: { trace: TraceResponse }): ReactElement {
  // Full "kind · reason" (or just "kind"). Shared by the pill text and its
  // `title` so the tooltip mirrors the visible text exactly — the reason can
  // be long enough to truncate in the header row, and the tooltip is the
  // overflow affordance.
  const verdictLabel = trace.verdict
    ? trace.verdict.reason
      ? `${trace.verdict.kind} · ${trace.verdict.reason}`
      : trace.verdict.kind
    : null
  return (
    <header
      className="border-b px-6 py-3 flex flex-col gap-2 text-sm"
      data-testid="kg-trace-header"
    >
      <div className="flex items-center gap-6">
        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">correlation_id</span>
          <span className="font-mono text-xs">{trace.correlation_id}</span>
        </div>
        {trace.git_sha && (
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">git_sha</span>
            <span className="font-mono text-xs" title={trace.git_sha}>
              {trace.git_sha.substring(0, 12)}
            </span>
          </div>
        )}
        {trace.duration_ms !== null && (
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">duration</span>
            <span className="font-mono text-xs tabular-nums">
              {(trace.duration_ms / 1_000).toFixed(2)}s
            </span>
          </div>
        )}
        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">steps</span>
          <span className="font-mono text-xs">{trace.steps.length}</span>
        </div>
        {trace.verdict && (
          <div className="flex flex-col min-w-0">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">verdict</span>
            <span
              className={
                'inline-block self-start max-w-[20rem] truncate px-1.5 py-0.5 rounded text-[11px] font-medium uppercase tracking-wide ' +
                (VERDICT_TONES[trace.verdict.kind] ?? VERDICT_TONE_DEFAULT)
              }
              data-testid="kg-trace-verdict"
              title={verdictLabel ?? undefined}
            >
              {verdictLabel}
            </span>
          </div>
        )}
      </div>
      <QueryAnswerStrip trace={trace} />
    </header>
  )
}

/**
 * Two-row mini-display of the user's question + the agent's answer
 * (AGNT-685 PR J). Always renders the labels for at-a-glance
 * scannability; falls back to a muted "(unavailable)" placeholder
 * when the backend's messages join missed (older traces, missing
 * conversation, db error). Long content is line-clamped to 3 lines
 * with a `title` for the full text on hover.
 */
function QueryAnswerStrip({ trace }: { trace: TraceResponse }): ReactElement {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
      <dt className="uppercase tracking-wide text-muted-foreground">query</dt>
      <dd
        className="font-mono whitespace-pre-wrap line-clamp-3"
        data-testid="kg-trace-query"
        title={trace.query ?? ''}
      >
        {trace.query ?? <span className="italic text-muted-foreground">(unavailable)</span>}
      </dd>
      <dt className="uppercase tracking-wide text-muted-foreground">final_answer</dt>
      <dd
        className="font-mono whitespace-pre-wrap line-clamp-3"
        data-testid="kg-trace-final-answer"
        title={trace.final_answer ?? ''}
      >
        {trace.final_answer ?? (
          <span className="italic text-muted-foreground">(unavailable)</span>
        )}
      </dd>
    </dl>
  )
}
